// src/components/ProtectedRoute.tsx
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CircularProgress, Box } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, roles }) => {
  const { isAuthenticated, user, checkAuthStatus } = useAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  
  useEffect(() => {
    const verifyAuth = async () => {
      // First check if we're already authenticated in state
      if (isAuthenticated && user) {
        setChecking(false);
        return;
      }
      
      // If not authenticated in state, check localStorage
      checkAuthStatus();
      setChecking(false);
    };
    
    verifyAuth();
  }, [isAuthenticated, user, checkAuthStatus]);
  
  // Show loading while checking authentication
  if (checking) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // If not authenticated, redirect to login with return URL
  if (!isAuthenticated) {
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(location.pathname)}`} />;
  }
  
  // If user doesn't have required role
  if (roles && user && !roles.includes(user.role)) {
    return <Navigate to="/unauthorized" />;
  }
  
  // User is authenticated and has required role
  return <>{children}</>;
};

export default ProtectedRoute;
